import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, inject } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PriceRequestCandidate, PriceRequestGroup, PriceRequestInbox } from '@shared/services/price-request-inbox.service';
import { ToastService } from '@shared/services/toast.service';

@Component({
  selector: 'app-price-request-refresh-modal',
  templateUrl: './price-request-refresh-modal.component.html',
  styleUrls: ['./price-request-refresh-modal.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, DatePipe],
})
export class PriceRequestRefreshModalComponent implements OnInit {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly inbox = inject(PriceRequestInbox);
  private readonly toast = inject(ToastService);
  private readonly cd = inject(ChangeDetectorRef);

  @Input({ required: true }) group!: PriceRequestGroup;

  selected = new Set<number>();
  sending = false;
  confirming = false;

  get candidates(): PriceRequestCandidate[] {
    return this.group.candidates;
  }

  get selectableCandidates(): PriceRequestCandidate[] {
    return this.candidates.filter(candidate => candidate.recipientEmails.length > 0);
  }

  ngOnInit(): void {
    this.selectableCandidates.forEach(candidate => this.selected.add(candidate.id));
  }

  toggle(candidate: PriceRequestCandidate): void {
    if (!candidate.recipientEmails.length) return;
    this.selected.has(candidate.id)
      ? this.selected.delete(candidate.id)
      : this.selected.add(candidate.id);
  }

  toggleAll(): void {
    if (this.allSelected) {
      this.selected.clear();
    } else {
      this.selectableCandidates.forEach(candidate => this.selected.add(candidate.id));
    }
  }

  get allSelected(): boolean {
    return this.selectableCandidates.length > 0
      && this.selected.size === this.selectableCandidates.length;
  }

  get someSelected(): boolean {
    return this.selected.size > 0 && this.selected.size < this.selectableCandidates.length;
  }

  send(): void {
    this.confirming = true;
    this.cd.markForCheck();
  }

  confirmSend(): void {
    const ids = Array.from(this.selected);
    if (!ids.length) return;
    this.sending = true;
    this.confirming = false;
    this.cd.markForCheck();

    this.inbox.send(this.group.kind, ids).subscribe({
      next: summary => {
        if (summary.failed.length === 0) {
          const subject = this.group.kind === 'handler' ? 'Handler requests' : 'Price requests';
          this.toast.success(`${subject} sent successfully`);
        } else {
          this.toast.error(`${summary.failed.length} request(s) failed. Check server logs.`);
        }
        this.sending = false;
        this.activeModal.close('sent');
      },
      error: () => {
        this.toast.error(`Failed to send ${this.group.itemLabel} requests.`);
        this.sending = false;
        this.cd.markForCheck();
      },
    });
  }

  cancelConfirm(): void {
    this.confirming = false;
    this.cd.markForCheck();
  }

  close(): void {
    this.activeModal.dismiss();
  }
}
